/*
 * SSF Detail — Client-side analytics beacon
 * Feeds the /admin6767 dashboard with real-time page-view and dwell-time
 * data. Skips admin routes so admin browsing doesn't pollute the stats.
 *
 * How it works
 * ────────────
 * 1. Assign a persistent `ssf_sid` cookie (1-year TTL) → 1 cookie == 1 session.
 * 2. On every SPA route change → POST /api/analytics/pageview with UTMs + path.
 * 3. On `beforeunload` and `visibilitychange:hidden` → send a session-end
 *    beacon carrying dwell-time in ms so the backend can compute avg
 *    time-on-site.
 *
 * All requests are `keepalive` so they survive tab-close on Chrome/Safari.
 * All failures are swallowed — never crash the app for analytics.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SID_COOKIE = 'ssf_sid';

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
};

const getSid = () => {
  try {
    const m = document.cookie.match(/(?:^|;\s*)ssf_sid=([^;]+)/);
    if (m) return decodeURIComponent(m[1]);
    const sid = uuid();
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `${SID_COOKIE}=${encodeURIComponent(sid)}; max-age=${oneYear}; path=/; SameSite=Lax`;
    return sid;
  } catch { return uuid(); }
};

const readUtm = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get('utm_source') || undefined,
      utm_medium: p.get('utm_medium') || undefined,
      utm_campaign: p.get('utm_campaign') || undefined,
      utm_content: p.get('utm_content') || undefined,
      gclid: p.get('gclid') || undefined,
      fbclid: p.get('fbclid') || undefined,
    };
  } catch { return {}; }
};

const send = (url, body) => {
  try {
    const payload = JSON.stringify(body);
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch { /* swallow */ }
};

export const useAnalytics = () => {
  const location = useLocation();
  const enterRef = useRef(0);
  const lastPathRef = useRef('');

  useEffect(() => {
    const path = location.pathname + location.search;
    // Skip admin routes — admin browsing shouldn't inflate stats
    if (location.pathname.startsWith('/admin')) return;

    const now = Date.now();
    // Close out previous page dwell first
    if (lastPathRef.current && enterRef.current) {
      const duration = now - enterRef.current;
      if (duration > 300) {
        send(`${API}/analytics/session-end`, {
          session_id: getSid(),
          path: lastPathRef.current,
          duration_ms: duration,
        });
      }
    }

    // Fire the new pageview
    send(`${API}/analytics/pageview`, {
      session_id: getSid(),
      path: path,
      referrer: document.referrer || undefined,
      screen_w: window.innerWidth,
      device: window.innerWidth < 768 ? 'mobile' : (window.innerWidth < 1024 ? 'tablet' : 'desktop'),
      ...readUtm(),
    });
    enterRef.current = now;
    lastPathRef.current = path;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const flush = () => {
      if (!lastPathRef.current || !enterRef.current) return;
      const duration = Date.now() - enterRef.current;
      if (duration < 300) return;
      send(`${API}/analytics/session-end`, {
        session_id: getSid(),
        path: lastPathRef.current,
        duration_ms: duration,
      });
      enterRef.current = Date.now(); // reset so we don't double-count
    };
    const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
};
